/*

& Client-safe: the price breakdown every pricing route sends (lib/payments/featurePricing.ts `breakdownPayload`),
& for the website's components. No models here, so the browser bundle can import it.

*/

export interface PriceBreakdownPayload {
	currency: string;
	quantity: number;
	unit_price: number;
	base_amount: number;
	platform_fee: { type: "fixed" | "percentage"; value: number; amount: number };
	presales_support: number;
	offer: { offer_id: string; label: string; discount_amount: number } | null;
	tax_inclusive: boolean;
	tax: { tax_name: string; tax_percentage: number; tax_amount: number };
	sub_total: number;
	list_total: number;
	total: number;
}

/** An exact amount for a breakdown line ("₹1,000", "₹152.54"), never compacted. */
export function formatBreakdownAmount(amount: number, currency: string): string {
	const figure = Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
	return currency === "AED" ? `AED ${figure}` : `₹${figure}`;
}

/**
 * A paid action's pricing as a pricing route answers it: the switch and the breakdown (null when unknown), and since
 * store billing (1 Nov 2026) whether it is free for this business and its free ones left (a requirement claim).
 */
export interface PaidActionPricing {
	payment_enabled: boolean;
	breakdown: PriceBreakdownPayload | null;
	/** The server says this business's next one costs nothing (one of its free ones, or an offer). */
	is_free?: boolean;
	/** The business's free ones; null while payments are off. */
	free_allowance?: { limit: number; used: number; remaining: number } | null;
}

/**
 * True when the action has to be paid for first: payments are on, the business has no free one left, and it costs
 * something (an offer can make it free).
 */
export const needsPayment = (pricing: PaidActionPricing | null | undefined): boolean =>
	!!pricing?.payment_enabled && pricing.is_free !== true && (pricing.breakdown?.total || 0) > 0;
